import { DocumentNode, OperationVariables } from '@apollo/client';
import debounce from 'lodash/debounce';
import {
  ComponentType,
  forwardRef,
  FunctionComponent,
  PropsWithRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  BaseOption,
  BasicFilterSelect,
  MultiFilterSelect,
  MultiSelectProps,
  OnSelectHandler,
  SelectProps,
  SelectValueDisplayProps,
  SingleSelectProps,
} from 'ui/selects';
import noop from 'utils/noop';

import { PAGINATED_SELECT_TEST_ID, PAGINATED_SELECT_TRIGGER_TEST_ID } from './constants';
import { DefaultTrigger, FilterSelectSuffix } from './PaginatedSelect.styles';
import { useAvailableOptions } from './useAvailableOptions';
import { useSelectedItem } from './useSelectedItem';

export const PAGINATED_SELECT_PLACEHOLDER = {
  MULTI: 'Select Items',
  SINGLE: 'Select an Item',
  LOADING: 'Loading...',
};

/**
 * DocumentNode seems to be the output type of gql`...`
 * Renaming it to GqlQuery as it feels more intuitive.
 */
type GqlQuery = DocumentNode;

export type ItemResponse = {
  /** The value of the option */
  id: string;
  /** The name/label of the option */
  name: string;
};

export type OnChangeHandler = (
  value: string,
  details: {
    option: BaseOption<string>;
  },
) => void;

export type OnMultiChangeHandler = (
  value: string[],
  details: {
    option: BaseOption<string>[];
  },
) => void;

export type PaginatedDefaultProps = {
  isMulti?: boolean;
  /** Callback when the value changes */
  onChange?: OnChangeHandler;
} & SingleSelectProps;

export type PaginatedSingleProps = {
  /** Is this multiselect? */
  /** Callback when the value changes */
  onChange?: OnChangeHandler;
} & SingleSelectProps;

export type PaginatedMultiProps = {
  /** Is this multiselect? */
  /** Callback when the value changes */
  onChange?: OnMultiChangeHandler;
} & MultiSelectProps;

export type ParseSingleItem<TItem extends ItemResponse = ItemResponse> = (
  resp: unknown,
) => TItem | undefined;
export type ParseMultipleItems<TItem extends ItemResponse = ItemResponse> = (
  resp: unknown,
) => TItem[] | undefined;

export type PaginatedSelectQueryProps<TItem extends ItemResponse = ItemResponse> = {
  /** Query to fetch multiple items */
  fetchAllItemsQuery: GqlQuery;
  /** The initial variables to be used when fetching the items */
  fetchAllItemsVariables?: OperationVariables;
  /** Get the list of options from the `fetchItemsQuery` response */
  fetchAllItemsParser?: ParseMultipleItems<TItem>;
  /**
   * Query to fetch singular item
   * Used to fetch the name if 1 item is selected
   */
  fetchSelectedItemQuery: GqlQuery;
  /** Get the item from the `fetchItemQuery` response */
  fetchSelectedItemParser: ParseSingleItem<TItem>;
  parseOption?: (item: TItem) => BaseOption<string>;
};

export type BasePaginatedSelectProps = {
  defaultOptions?: BaseOption<string>[];
  /** Does this component contain an error */
  hasError?: boolean;
  /** Is this multiselect? */
  isMulti?: boolean;
  /** Custom trigger */
  Trigger?: FunctionComponent<React.PropsWithChildren<any>>;
  /** Current value of the component */
  value?: string | string[];
  /* Function to set tooltip if disabled */
  setTooltipContent?: (content: string) => void;
} & Omit<SelectProps<string>, 'options' | 'onSelect'>;

export type PaginatedSelectProps<TItem extends ItemResponse = ItemResponse> =
  BasePaginatedSelectProps &
    PaginatedSelectQueryProps<TItem> &
    (PaginatedDefaultProps | PaginatedSingleProps | PaginatedMultiProps);

export function parsePaginatedListItem({ name, id: optionId }: ItemResponse): BaseOption<string> {
  return {
    id: optionId,
    label: name,
    value: `${optionId}`,
    Suffix: <FilterSelectSuffix>{optionId}</FilterSelectSuffix>,
  };
}

function identity<T = any, R = T>(value: T): R {
  return value as unknown as R;
}

const onSingleSelect =
  (onChange: OnChangeHandler): OnSelectHandler =>
  (currentSelection, { selectedOptions }) => {
    onChange(currentSelection[0], { option: selectedOptions[0] });
  };

const onMultiSelect =
  (onChange: OnMultiChangeHandler): OnSelectHandler =>
  (currentSelection, { selectedOptions }) => {
    onChange(currentSelection, { option: selectedOptions });
  };

function getDefaultValues(value?: string | string[]): string[] {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

const getPlaceholder = (placeholder?: string, isLoading = false, isMulti = false) => {
  if (isLoading) return PAGINATED_SELECT_PLACEHOLDER.LOADING;
  if (placeholder) return placeholder;
  return isMulti ? PAGINATED_SELECT_PLACEHOLDER.MULTI : PAGINATED_SELECT_PLACEHOLDER.SINGLE;
};

export function PaginatedSelect<TItem extends ItemResponse = ItemResponse>({
  defaultOptions = [],
  fetchAllItemsParser = identity,
  fetchAllItemsQuery,
  fetchAllItemsVariables = { search: '' },
  fetchSelectedItemQuery,
  fetchSelectedItemParser,
  hasError = false,
  isMulti = false,
  parseOption = parsePaginatedListItem,
  placeholder,
  Trigger = DefaultTrigger as FunctionComponent<React.PropsWithChildren<any>> &
    ComponentType<React.PropsWithChildren<PropsWithRef<SelectValueDisplayProps<string>>>>,
  value,
  // callbacks
  onChange = noop,
  setTooltipContent = noop,
  ...props
}: PaginatedSelectProps<TItem>) {
  const [selectedItem, setSelectedItem] = useState<string | undefined>();
  const [options, setOptions] = useState<BaseOption<string>[]>(defaultOptions);
  const [searchQuery, setSearchQuery] = useState<string>();

  const defaultValue = useMemo(() => getDefaultValues(value), [value]);

  const optionVariables = useMemo(() => {
    const baseValues = fetchAllItemsVariables ?? {};

    return {
      ...baseValues,
      search: searchQuery,
    };
  }, [searchQuery, fetchAllItemsVariables, defaultValue]);

  const {
    results: availableOptions,
    error: hasGqlError,
    isLoading: isQueryLoading,
  } = useAvailableOptions({
    query: fetchAllItemsQuery,
    variables: optionVariables,
    // TODO: Fix the incoming prop for this
    parseResponse: (resp: unknown) => {
      const data = fetchAllItemsParser(resp);

      return data ? data.map(parseOption) : [];
    },
  });

  useEffect(() => {
    setOptions(availableOptions);
  }, [availableOptions]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(undefined);
    }
  }, [value]);

  const itemId = useMemo(() => {
    if (!value) {
      return undefined;
    }

    const id = Array.isArray(value) ? value[0] : value;
    return parseInt(id, 10);
  }, [value]);

  // This will get the name of the item, even if it is not in the current list
  const {
    result: itemResult,
    error: hasItemError,
    isLoading: isItemLoading,
  } = useSelectedItem({
    query: fetchSelectedItemQuery,
    id: itemId,
    parseResponse: (resp: unknown) => {
      const data = fetchSelectedItemParser(resp);

      return data?.name;
    },
  });

  useEffect(() => {
    if (isItemLoading) {
      return;
    }

    const multiVal = Array.isArray(value)
      ? `${value.length} ${value.length === 1 ? 'option' : 'options'} selected`
      : '';
    const singleVal = hasItemError ? (value as string) : itemResult;
    const selectedItem = isMulti ? multiVal : singleVal;

    setSelectedItem(selectedItem);
  }, [isItemLoading, hasItemError, itemResult, isMulti, value]);

  useEffect(() => {
    if (selectedItem) {
      setTooltipContent(selectedItem);
    }
  }, [selectedItem]);

  const Select = isMulti ? MultiFilterSelect : BasicFilterSelect;
  const handleSelect = isMulti ? onMultiSelect : onSingleSelect;

  return (
    <Select
      data-test={PAGINATED_SELECT_TEST_ID}
      {...(props as SelectProps<string> & MultiSelectProps & SingleSelectProps)}
      defaultValues={defaultValue}
      disableInternalFiltering
      isError={!isQueryLoading && (hasError || hasGqlError)}
      onSearchChange={debounce(setSearchQuery, 300)}
      onSelect={handleSelect(onChange as OnChangeHandler & OnMultiChangeHandler)}
      options={options}
      placeholder={getPlaceholder(placeholder, isItemLoading, isMulti)}
      // need to use forwardRef for the toggle props to correctly be passed to
      // this component
      Trigger={forwardRef((triggerProps, triggerRef) => {
        const commonProps = {
          dataTest: PAGINATED_SELECT_TRIGGER_TEST_ID,
          ...triggerProps,
          ref: triggerRef,
        };

        const displayedItem = defaultValue.length > 1 ? undefined : selectedItem;
        const customText = isItemLoading ? PAGINATED_SELECT_PLACEHOLDER.LOADING : displayedItem;

        return customText ? (
          <Trigger {...commonProps}>{customText}</Trigger>
        ) : (
          <Trigger {...commonProps} />
        );
      })}
    />
  );
}

export default PaginatedSelect;
